import CheckIcon from '@mui/icons-material/Check';
import DangerousIcon from '@mui/icons-material/Dangerous';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { FormControl, FormControlLabel, Radio, RadioGroup, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import React, { FC, memo, useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

import AdornmentTooltip from './AdornmentTooltip/AdornmentTooltip';
import FlashMessageCreate from './FlashMessage';
import Input from './Input/Input';
import { TMriData } from './RegistrationModalMri';
import RegistrationModalMriResultImg from './RegistrationModalMriResultImg';
import RegistrationModalMriResultTable from './RegistrationModalMriResultTable';

import API from '../API';
import { MainModalCreate } from '../MainModal';
import { Colors, FlashMessageType } from '../const';
import { FormValuesFromFields, genderTable, InputFields, normalizeAndValidateDate, normalizeToHalfWidth, RawField, withValidation } from '../forms';
import { MriViewType } from '../types';

export type TMatchPatient = {
  birthdate: string;
  gender: 'male' | 'female' | 'other';
  hospitalPatientId: string;
  kanjiName: string;
  patientName: string;
  patientId: number;
  prepaidCode: string;
  brainID: number;
};

type TRegistrationModalMriResult = {
  mriData: TMriData;
  setIsBehind: (value: boolean) => void;
  isDisabled: boolean;
  setIsDisabled: (value: boolean) => void;
  setBeforeClose: (value: { flag: boolean; message: string }) => void;
  removeModal: (value?: boolean) => void;
};

const EmbeddedReportStatus = {
  accepted: <CheckIcon color='success' />,
  rejected: <DangerousIcon color='error' />,
  'not-provided': <WarningAmberIcon color='warning' />,
};

const warningMessage = 'このページを閉じると、現在のアップロードが無効になります。<br/>よろしいですか？';

const RegistrationModalMriResult: FC<TRegistrationModalMriResult> = ({
  setIsBehind,
  mriData,
  isDisabled,
  setIsDisabled,
  setBeforeClose,
  removeModal,
}) => {
  const FormFields = {
    hospitalPatientId: { ...InputFields.hospitalPatientId, required: true },
    patientName: { ...InputFields.patientName, required: !window.__GlobalVars.hospitalAnonymizeDataToggle },
    gender: { ...InputFields.gender, required: true },
    birthdate: { ...InputFields.birthdate, required: true },
    patientId: { ...InputFields.patientId },
    prepaidCode: { ...InputFields.prepaidCode },
  } as const satisfies Record<string, RawField>;

  type PatientFormValues = FormValuesFromFields<typeof FormFields>;

  const [skipUpdate, setSkipUpdate] = useState<boolean>(false);
  const [selected, setSelected] = useState<number | undefined>();
  const [matchPatients, setMatchPatients] = useState<Array<TMatchPatient>>([]);
  const methods = useForm<PatientFormValues>({
    defaultValues: {
      hospitalPatientId: mriData.hospitalPatientId || '',
      patientName: mriData.name || '',
      gender: mriData.gender || 'other',
      birthdate: mriData.birthDate || '',
      patientId: '',
      prepaidCode: '',
    },
  });
  const {
    handleSubmit,
    formState: { errors },
    getValues,
    clearErrors,
    control,
  } = methods;
  const watched = useWatch({
    control,
    name: ['hospitalPatientId', 'patientName', 'gender', 'birthdate', 'patientId'],
  });
  const onSubmit = withValidation(FormFields, methods, () => {
    const formData = new FormData();
    const values = getValues();

    for (const [key, value] of Object.entries(mriData)) {
      formData.append(key, value as string);
    }

    for (const [key, value] of Object.entries(values)) {
      if (formData.has(key)) {
        formData.set(key, value);
      } else {
        formData.append(key, value);
      }
    }

    if (selected) formData.set('patientId', `${selected}`);
    const match = matchPatients?.find(({ patientId }) => patientId === selected);

    setIsBehind(true);
    setIsDisabled(true);
    setSkipUpdate(true);

    MainModalCreate({
      modalName: 'registrationModalMriResultConfirmation',
      patientName: match ? (match.patientName.length ? `${match.patientName}` : match.kanjiName) : values.patientName,
      birthDate: match ? match.birthdate : values.birthdate,
      hospitalPatientId: match ? (match.hospitalPatientId ? match.hospitalPatientId : values.hospitalPatientId) : values.hospitalPatientId,
      patientId: match ? match.patientId : selected,
      gender: match ? match.gender : values.gender,
      prepaidCode: values.prepaidCode,
      onAccept: (removeModalCb, disableModalCb) => {
        if (disableModalCb) disableModalCb(true);

        let flashMessage = '';
        let flashType = FlashMessageType.Success;
        let timer: NodeJS.Timeout;

        API[selected ? 'setVisit' : 'setPatient'](formData)
          .then(({ data }) => {
            setBeforeClose({ flag: false, message: warningMessage });
            flashMessage = data.msgUser;
            removeModal(true);
            window.PatientTable.update();
            timer = setTimeout(() => {
              window.location.href = `${window.location.href}/${data.patientId}/${data.visitId}`;
            }, data.seconds);
          })
          .catch((err) => {
            flashMessage = err.response.data.msgUser;
            flashType = FlashMessageType.Error;
          })
          .finally(() => {
            FlashMessageCreate({
              message: flashMessage,
              type: flashType,
              onReject: () => clearTimeout(timer),
              rejectMessage: 'やめる',
              isSelfDestroyable: true,
            });
            if (removeModalCb) removeModalCb();
          });
      },
      onReject: () => {
        setIsBehind(false);
        setIsDisabled(false);
        setSkipUpdate(false);
      },
    });
  });

  const handleFlashMessage = (hintMsg: Array<string>) => {
    FlashMessageCreate({
      message: `${hintMsg.map((msg) => ` - <b>${msg}</b><br>`).join('')}`,
      type: FlashMessageType.Warning,
      isSelfDestroyable: false,
      onAccept: () => {},
    });
  };

  useEffect(() => {
    if (mriData.hintMsg?.length) {
      handleFlashMessage(mriData.hintMsg);
    }
  }, [mriData.hintMsg]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setBeforeClose({ flag: true, message: warningMessage }), []);
  useEffect(() => {
    if (skipUpdate) return;

    const timeoutId = setTimeout(() => {
      const values = getValues();

      API.getMatchPatients<any, Array<TMatchPatient>>(values).then(({ data }) => {
        setSelected(undefined);
        setMatchPatients(data);

        const match = data.find(
          ({ hospitalPatientId, patientId }) => values.hospitalPatientId === hospitalPatientId || parseInt(values.patientId, 10) === patientId,
        );

        if (match) setSelected(match.patientId);
      });
    }, 1000);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watched, skipUpdate]);

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-evenly',
          borderRadius: '4px',
          overflow: 'hidden',
          backgroundColor: Colors.black,
          flexShrink: 0,
        }}
      >
        <RegistrationModalMriResultImg type={MriViewType.RAW_AX} uuid={mriData.uuidNii || ''} />
        <RegistrationModalMriResultImg type={MriViewType.RAW_COR} uuid={mriData.uuidNii || ''} />
        <RegistrationModalMriResultImg type={MriViewType.RAW_SAG} uuid={mriData.uuidNii || ''} />
        <RegistrationModalMriResultImg type={MriViewType.RAW_ANIM} uuid={mriData.uuidNii || ''} />
      </div>
      <form
        style={{
          display: 'grid',
          gridTemplateAreas: `'primary secondary'
          'lPixelReport lPixelReport'
          `,
          gridGap: '1rem',
          margin: '1rem 0',
          fontSize: '0.875rem',
          gridTemplateColumns: '7fr 3fr',
          width: '100%',
        }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div style={{ gridArea: 'primary', border: '1px solid var(--color-cerulean)' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: '#E3F6FF',
              padding: '0 0.5rem',
              color: 'var(--color-cerulean)',
              fontWeight: 700,
            }}
          >
            受診者情報（DICOMタグより取得）
            <span style={{ fontWeight: 'initial', padding: '0.15rem 0.4rem 0.25rem', color: Colors.white, backgroundColor: Colors.red }}>必須</span>
            {mriData.hintMsg?.length ? (
              <Tooltip title='Warnings' arrow>
                <IconButton color='warning' onClick={() => handleFlashMessage(mriData.hintMsg as Array<string>)}>
                  <WarningAmberIcon />
                </IconButton>
              </Tooltip>
            ) : null}
          </div>
          <div
            style={{
              display: 'grid',
              gap: '1rem',
              alignItems: 'start',
              padding: '1rem',
              gridTemplateAreas: `
              "patientName hospitalPatientId"
              "birthdate gender"
            `,
            }}
          >
            <Controller
              name={FormFields.hospitalPatientId.name}
              control={control}
              rules={{ required: FormFields.hospitalPatientId.required }}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  label={FormFields.hospitalPatientId.label}
                  errorText={fieldState.error?.message}
                  disabled={isDisabled}
                  inputProps={{
                    tabIndex: FormFields.hospitalPatientId.tabIndex,
                    required: FormFields.hospitalPatientId.required,
                    maxLength: FormFields.hospitalPatientId.maxLength,
                    pattern: FormFields.hospitalPatientId.pattern,
                  }}
                  type={FormFields.hospitalPatientId.type}
                  size='small'
                  fullWidth
                  onBlur={() => {
                    field.onBlur();
                    normalizeToHalfWidth<PatientFormValues>(FormFields.hospitalPatientId.name, methods);
                  }}
                  onChange={(evt) => {
                    field.onChange(evt);
                    clearErrors(FormFields.hospitalPatientId.name);
                  }}
                  style={{ gridArea: 'hospitalPatientId' }}
                />
              )}
            />
            <Controller
              name={FormFields.patientName.name}
              control={control}
              rules={{ required: FormFields.patientName.required }}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  label={FormFields.patientName.label}
                  errorText={fieldState.error?.message}
                  disabled={isDisabled}
                  inputProps={{
                    tabIndex: FormFields.patientName.tabIndex,
                    required: FormFields.patientName.required,
                    maxLength: FormFields.patientName.maxLength,
                    pattern: FormFields.patientName.pattern,
                  }}
                  type={FormFields.patientName.type}
                  size='small'
                  fullWidth
                  style={{ gridArea: 'patientName' }}
                />
              )}
            />
            <Controller
              name={FormFields.birthdate.name}
              control={control}
              rules={{ required: FormFields.birthdate.required }}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  label={FormFields.birthdate.label}
                  errorText={fieldState.error?.message}
                  disabled={isDisabled}
                  inputProps={{
                    tabIndex: FormFields.birthdate.tabIndex,
                    required: FormFields.birthdate.required,
                  }}
                  type={FormFields.birthdate.type}
                  size='small'
                  fullWidth
                  onBlur={() => {
                    field.onBlur();
                    normalizeAndValidateDate<PatientFormValues>(FormFields.birthdate.name, methods);
                  }}
                  onChange={(evt) => {
                    field.onChange(evt);
                    clearErrors(FormFields.birthdate.name);
                  }}
                  style={{ gridArea: 'birthdate' }}
                />
              )}
            />
            <FormControl variant='outlined' size='small' error={!!errors.gender} disabled={isDisabled} style={{ gridArea: 'gender' }}>
              <Controller
                name={FormFields.gender.name}
                control={control}
                rules={{ required: FormFields.gender.required }}
                render={({ field }) => (
                  <RadioGroup sx={{ justifyContent: 'start' }} row {...field}>
                    <FormControlLabel
                      value='male'
                      control={<Radio required={FormFields.gender.required} inputProps={{ tabIndex: FormFields.gender.tabIndex }} />}
                      label={genderTable.male}
                    />
                    <FormControlLabel
                      value='female'
                      control={<Radio required={FormFields.gender.required} inputProps={{ tabIndex: FormFields.gender.tabIndex }} />}
                      label={genderTable.female}
                    />
                    <FormControlLabel
                      value='other'
                      control={<Radio required={FormFields.gender.required} inputProps={{ tabIndex: FormFields.gender.tabIndex }} />}
                      label={genderTable.other}
                    />
                  </RadioGroup>
                )}
              />
            </FormControl>
          </div>
        </div>

        <div style={{ gridArea: 'secondary', border: '1px solid var(--color-mercury)' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              minHeight: '40px',
              background: 'var(--color-mercury)',
              padding: '0 0.5rem',
              boxSizing: 'border-box',
              fontWeight: 700,
            }}
          >
            BrainSuite情報
            <span style={{ fontWeight: 'initial', padding: '0.15rem 0.4rem 0.25rem', color: Colors.white, backgroundColor: Colors.gray }}>任意</span>
          </div>
          <div
            style={{
              display: 'grid',
              gap: '1rem',
              padding: '1rem',
            }}
          >
            <Controller
              name={FormFields.patientId.name}
              control={control}
              rules={{ required: FormFields.patientId.required }}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  label={FormFields.patientId.label}
                  errorText={fieldState.error?.message}
                  disabled={isDisabled}
                  inputProps={{
                    tabIndex: FormFields.patientId.tabIndex,
                    required: FormFields.patientId.required,
                    maxLength: FormFields.patientId.maxLength,
                    pattern: FormFields.patientId.pattern,
                  }}
                  type={FormFields.patientId.type}
                  size='small'
                  fullWidth
                  customAdornments={[
                    <AdornmentTooltip
                      key='help'
                      type='help'
                      title={
                        <>
                          Brain-IDをお持ちの方は、Brain-IDを入力してください。Brain-IDは、BrainSuiteを始めとした各種サービスの共通アカウントです。
                          Brain-IDを入力することで、過去の解析結果を同じアカウントでまとめて表示することができます。
                        </>
                      }
                    />,
                  ]}
                />
              )}
            />
            <Controller
              name={FormFields.prepaidCode.name}
              control={control}
              rules={{ required: FormFields.prepaidCode.required }}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  label={FormFields.prepaidCode.label}
                  errorText={fieldState.error?.message}
                  disabled={isDisabled}
                  inputProps={{
                    tabIndex: FormFields.prepaidCode.tabIndex,
                    required: FormFields.prepaidCode.required,
                    maxLength: FormFields.prepaidCode.maxLength,
                    pattern: FormFields.prepaidCode.pattern,
                  }}
                  type={FormFields.prepaidCode.type}
                  size='small'
                  fullWidth
                  customAdornments={[
                    <AdornmentTooltip
                      key='help'
                      type='help'
                      title={
                        <>
                          BrainSuiteチケットをお持ちの方は、チケット番号を入力してください。
                          事前にBrainSuiteをご購入およびお支払いしている方には、BrainSuiteチケットをお配りしております。
                          チケット番号を入力することで、ご利用者様の窓口負担金を調整いたします。
                        </>
                      }
                    />,
                  ]}
                />
              )}
            />
          </div>
        </div>

        {mriData.embeddedLpixelReport === 'not-provided' ? null : (
          <FormControl sx={{ gridArea: 'lPixelReport' }} variant='outlined' size='small' disabled={isDisabled}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              EIRLレポートが含まれています。
              {EmbeddedReportStatus[mriData.embeddedLpixelReport]}
            </div>
          </FormControl>
        )}

        <input className='visually-hidden' type='submit' id='result-table-submit' formNoValidate={!!selected} />
      </form>
      <>
        <RegistrationModalMriResultTable matchPatients={matchPatients} setSelected={setSelected} selected={selected} isDisabled={isDisabled} />
        <div
          style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            padding: '1rem',
            backgroundColor: 'white',
            zIndex: 1000,
            border: '1px solid var(--color-cerulean)',
            borderTop: 'none',
          }}
        >
          <label
            htmlFor='result-table-submit'
            style={{ width: 'max-content' }}
            className={`btn btn--additional ${isDisabled || !selected ? 'btn--disabled' : null}`}
          >
            受診履歴を追加
          </label>
          <label
            htmlFor='result-table-submit'
            style={{ width: 'max-content' }}
            className={`btn btn--main ${isDisabled || !!selected ? 'btn--disabled' : null}`}
          >
            新規受診を登録
          </label>
        </div>
      </>
    </>
  );
};

export default memo(RegistrationModalMriResult);
